import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Languages, Plane, HeartHandshake, ArrowRight, CheckCircle2,
  Clock, Coins, ListChecks, Sparkles,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";

export const Route = createFileRoute("/tools")({
  component: ToolsHub,
  head: () => {
    const site = getActiveSiteUrl();
    const base = pageHeadForProvince({
      title: "เครื่องมือคำนวณ Visa / แปลเอกสาร / สมรส (ฟรี)",
      description:
        "เครื่องมือฟรี: คำนวณค่าแปลเอกสาร, แนะนำวีซ่าที่เหมาะ (DTV/LTR/Elite/Retirement), และเช็คลิสต์เอกสารสมรสไทย-ต่างชาติ — ใช้งานทันทีไม่ต้องลงทะเบียน",
      path: "/tools",
      type: "website",
    });
    const faqs = [
      { q: "เครื่องมือพวกนี้ใช้ฟรีจริงไหม?", a: "ใช้ฟรี 100% ไม่ต้องลงทะเบียน คำนวณในเบราว์เซอร์ของคุณ ผลลัพธ์เป็นค่าประมาณตามอัตราตลาดและตารางราคา Thai Notary 2026" },
      { q: "ราคาที่คำนวณตรงกับใบเสนอราคาจริงไหม?", a: "ตรงประมาณ 90-95% สำหรับเคสมาตรฐาน — เคสซับซ้อนอาจต่างได้ ส่งเอกสารให้เราประเมินผ่าน LINE @THAINOTARY เพื่อรับใบเสนอราคาตามเอกสารจริง โดยทั่วไปตอบกลับภายในเวลาทำการ" },
      { q: "เครื่องมือแนะนำวีซ่าใช้เกณฑ์อะไร?", a: "ใช้เกณฑ์ทางการของ Royal Thai Embassy + LTR Office + Thailand Privilege Card ปี 2026 พิจารณาจากอายุ รายได้ ทรัพย์สิน ลักษณะงาน และระยะเวลาที่ต้องการพำนัก" },
      { q: "เช็คลิสต์เอกสารสมรสครอบคลุมทุกสัญชาติไหม?", a: "ครอบคลุม 10+ สัญชาติหลัก (US, UK, Australia, Germany, France, Japan, China, India, Korea, Russia) แต่ละสัญชาติต้องใช้ Affirmation/Affidavit of Single Status จากสถานทูตของตน" },
    ];
    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildBreadcrumbSchema([{ name: "หน้าแรก", url: "/" }, { name: "เครื่องมือคำนวณ", url: "/tools" }])) },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "ItemList", name: "Free Calculators & Tools",
          itemListElement: [
            { "@type": "ListItem", position: 1, url: site + "/tools#translation", name: "Translation Cost Calculator" },
            { "@type": "ListItem", position: 2, url: site + "/tools#visa", name: "Visa Recommendation Engine" },
            { "@type": "ListItem", position: 3, url: site + "/tools#marriage", name: "Marriage Document Checklist" },
          ],
        }) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/tools")) },
      ],
    };
  },
});

type TabKey = "translation" | "visa" | "marriage";

function ToolsHub() {
  const [tab, setTab] = useState<TabKey>("translation");
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-6xl px-4 py-14">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gold">
              <Sparkles className="h-3.5 w-3.5" /> Free tools · ไม่ต้องสมัคร
            </div>
            <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">เครื่องมือคำนวณ Visa / แปลเอกสาร / สมรส</h1>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
              ประเมินค่าใช้จ่ายและเอกสารด้วยตัวเอง คำนวณในเบราว์เซอร์ ไม่เก็บข้อมูล เหมาะสำหรับ Expat, Digital Nomad และคู่รักไทย-ต่างชาติที่อยากรู้คร่าวๆ ก่อนตัดสินใจ
            </p>
            <section id="ai-answer-tldr" className="mt-6 max-w-3xl rounded-2xl border-2 border-primary/20 bg-primary/5 p-5">
              <p className="text-[11px] font-bold uppercase tracking-widest text-primary">TL;DR</p>
              <p className="mt-2 text-sm leading-relaxed">
                Thai Notary มีเครื่องมือฟรี 3 ตัว: <strong>(1) Translation Cost Calculator</strong> คำนวณค่าแปลตามหน้าและภาษา,
                <strong> (2) Visa Recommendation Engine</strong> แนะนำวีซ่าที่เหมาะ (DTV/LTR/Elite/Retirement),
                <strong> (3) Marriage Document Checklist</strong> สร้างรายการเอกสารตามสัญชาติคู่สมรสต่างชาติ
              </p>
            </section>
          </div>
        </section>

        <section className="sticky top-0 z-20 border-b border-border bg-background/95 backdrop-blur">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 px-4 py-3">
            <TabBtn current={tab} value="translation" onClick={setTab} Icon={Languages} label="ค่าแปลเอกสาร" />
            <TabBtn current={tab} value="visa" onClick={setTab} Icon={Plane} label="แนะนำวีซ่า" />
            <TabBtn current={tab} value="marriage" onClick={setTab} Icon={HeartHandshake} label="เช็คลิสต์สมรส" />
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-6xl px-4 py-12">
            {tab === "translation" && <TranslationCalc />}
            {tab === "visa" && <VisaRecommender />}
            {tab === "marriage" && <MarriageChecklist />}
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-6xl px-4 py-12">
            <h2 className="text-2xl font-extrabold">ต้องการใบเสนอราคาแม่นยำ?</h2>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">ส่งภาพเอกสารผ่าน LINE @THAINOTARY ทีมประเมินให้ภายใน 30 นาที</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground hover:bg-primary/90">ติดต่อทีมงาน <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/pricing-matrix" className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-5 py-2.5 text-sm font-bold text-foreground hover:border-gold">ดูตารางราคาเต็ม</Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function TabBtn({ current, value, onClick, Icon, label }: { current: TabKey; value: TabKey; onClick: (v: TabKey) => void; Icon: React.ComponentType<{ className?: string }>; label: string }) {
  const active = current === value;
  return (
    <button onClick={() => onClick(value)} className={"inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition " + (active ? "border-gold bg-gold text-foreground" : "border-border bg-card text-muted-foreground hover:border-gold")}>
      <Icon className="h-3.5 w-3.5" />{label}
    </button>
  );
}

const TRANSLATION_LANG_RATE: Record<string, { label: string; rate: number }> = {
  en: { label: "อังกฤษ (EN)", rate: 500 },
  zh: { label: "จีน (ZH)", rate: 700 },
  ja: { label: "ญี่ปุ่น (JA)", rate: 800 },
  ko: { label: "เกาหลี (KO)", rate: 800 },
  de: { label: "เยอรมัน (DE)", rate: 900 },
  fr: { label: "ฝรั่งเศส (FR)", rate: 900 },
  ru: { label: "รัสเซีย (RU)", rate: 900 },
  ar: { label: "อาหรับ (AR)", rate: 1200 },
  other: { label: "ภาษาอื่นๆ (40+)", rate: 1100 },
};

const CERT_TYPE: Record<string, { label: string; add: number; days: string }> = {
  none: { label: "ไม่รับรอง (Plain)", add: 0, days: "1-2 วัน" },
  moj: { label: "MoJ ผู้แปลขึ้นทะเบียน", add: 800, days: "2-3 วัน" },
  naati: { label: "NAATI (Australia)", add: 1500, days: "3-5 วัน" },
  consular: { label: "+ กงสุล MFA", add: 2000, days: "+5-7 วัน" },
  apostille: { label: "+ Apostille (2026)", add: 2500, days: "+5-10 วัน" },
};

function TranslationCalc() {
  const [pages, setPages] = useState(3);
  const [lang, setLang] = useState("en");
  const [cert, setCert] = useState("moj");
  const [urgent, setUrgent] = useState(false);
  const result = useMemo(() => {
    const base = pages * TRANSLATION_LANG_RATE[lang].rate;
    const certCost = CERT_TYPE[cert].add * Math.max(1, Math.ceil(pages / 5));
    const urgentFee = urgent ? Math.round((base + certCost) * 0.3) : 0;
    return { base, certCost, urgentFee, total: base + certCost + urgentFee };
  }, [pages, lang, cert, urgent]);
  return (
    <div id="translation" className="grid gap-8 lg:grid-cols-[1.1fr_1fr]">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary"><Languages className="h-3.5 w-3.5" /> Tool #1</div>
        <h2 className="mt-2 text-2xl font-extrabold">คำนวณค่าแปลเอกสาร</h2>
        <p className="mt-2 text-sm text-muted-foreground">ใส่จำนวนหน้า ภาษาเป้าหมาย และประเภทรับรอง ระบบคำนวณราคาประมาณตามอัตรา Thai Notary 2026</p>
        <div className="mt-6 space-y-5 rounded-2xl border border-border bg-card p-6">
          <div>
            <label className="text-sm font-semibold">จำนวนหน้า: {pages} หน้า</label>
            <input type="range" min={1} max={50} value={pages} onChange={(e) => setPages(Number(e.target.value))} className="mt-2 w-full" />
          </div>
          <div>
            <label className="text-sm font-semibold">ภาษาปลายทาง</label>
            <select value={lang} onChange={(e) => setLang(e.target.value)} className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              {Object.entries(TRANSLATION_LANG_RATE).map(([k, v]) => (<option key={k} value={k}>{v.label} — ฿{v.rate}/หน้า</option>))}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold">ประเภทรับรอง</label>
            <select value={cert} onChange={(e) => setCert(e.target.value)} className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              {Object.entries(CERT_TYPE).map(([k, v]) => (<option key={k} value={k}>{v.label} (+฿{v.add}/ชุด)</option>))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} className="h-4 w-4 rounded border-input" />
            <span>งานด่วน &lt;24 ชม. (+30%)</span>
          </label>
        </div>
      </div>
      <aside className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-6">
        <div className="text-xs font-bold uppercase tracking-widest text-primary">ราคาประมาณ</div>
        <div className="mt-2 text-5xl font-black text-foreground">฿{result.total.toLocaleString()}</div>
        <div className="mt-1 text-xs text-muted-foreground">ประมาณการ ไม่รวมค่าจัดส่ง บวก VAT 7% หากออกใบกำกับภาษี</div>
        <div className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" /> {CERT_TYPE[cert].days}{urgent && " · เร่งด่วน"}</div>
        <dl className="mt-5 space-y-2 text-sm">
          <Row label={"ค่าแปล " + pages + " หน้า × ฿" + TRANSLATION_LANG_RATE[lang].rate} value={"฿" + result.base.toLocaleString()} />
          <Row label="ค่ารับรอง" value={"฿" + result.certCost.toLocaleString()} />
          {result.urgentFee > 0 && (<Row label="ค่าด่วน (+30%)" value={"฿" + result.urgentFee.toLocaleString()} />)}
        </dl>
        <Link to="/contact" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:bg-primary/90">ขอใบเสนอราคาแม่นยำ <ArrowRight className="h-4 w-4" /></Link>
      </aside>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/50 pb-1.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-semibold text-foreground">{value}</dd>
    </div>
  );
}

interface VisaOpt { name: string; why: string; duration: string; fee: string }

function recommendVisa(p: { age: number; income: number; assets: number; remote: boolean; years: number }): { top: VisaOpt; alts: VisaOpt[] } {
  const opts: { score: number; opt: VisaOpt }[] = [];
  if (p.assets >= 1_000_000 && p.income >= 80_000 / 12) opts.push({ score: 100, opt: { name: "LTR Wealthy Global Citizen", why: "ทรัพย์สิน ≥$1M + รายได้ปี ≥$80k สิทธิครบ Work Permit + ภาษีพิเศษ 17%", duration: "10 ปี", fee: "฿50,000" } });
  if (p.age >= 50 && p.income >= 80_000 / 12) opts.push({ score: 95, opt: { name: "LTR Wealthy Pensioner", why: "อายุ ≥50 + รายได้บำนาญ ≥$80k/ปี พำนัก 10 ปี", duration: "10 ปี", fee: "฿50,000" } });
  if (p.remote && p.income >= 80_000 / 12 && p.years >= 5) opts.push({ score: 90, opt: { name: "LTR Work-from-Thailand", why: "Remote worker จ้างโดยบริษัทต่างประเทศ Top 500", duration: "10 ปี", fee: "฿50,000" } });
  if (p.assets >= 300_000 && p.years >= 5) opts.push({ score: 80, opt: { name: "Thailand Privilege Gold", why: "จ่ายครั้งเดียวได้ 5 ปี ไม่ต้องพิสูจน์รายได้ มี Fast Track", duration: "5 ปี", fee: "฿900,000" } });
  if (p.age >= 50 && p.years >= 1) opts.push({ score: 85, opt: { name: "Non-Immigrant O-A (Retirement)", why: "อายุ ≥50 เงินฝาก ฿800k หรือรายได้ ฿65k/เดือน", duration: "1 ปี (ต่อได้)", fee: "฿15,000" } });
  if (p.remote && p.age >= 20) opts.push({ score: p.income >= 500 ? 75 : 60, opt: { name: "DTV (Destination Thailand Visa)", why: "Digital Nomad / Remote worker พำนักครั้งละ 180 วัน × 5 ปี", duration: "5 ปี (180/ครั้ง)", fee: "฿18,000" } });
  opts.push({ score: 20, opt: { name: "Tourist Visa / Visa Exemption", why: "เริ่มต้นง่าย 60 วัน + ต่อ 30 วัน เหมาะกับการมาดูลู่ทางก่อน", duration: "60-90 วัน", fee: "฿2,000" } });
  opts.sort((a, b) => b.score - a.score);
  return { top: opts[0].opt, alts: opts.slice(1, 4).map((o) => o.opt) };
}

function VisaRecommender() {
  const [age, setAge] = useState(35);
  const [income, setIncome] = useState(80_000);
  const [assets, setAssets] = useState(500_000);
  const [remote, setRemote] = useState(true);
  const [years, setYears] = useState(2);
  const rec = useMemo(() => recommendVisa({ age, income, assets, remote, years }), [age, income, assets, remote, years]);
  return (
    <div id="visa" className="grid gap-8 lg:grid-cols-[1.1fr_1fr]">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary"><Plane className="h-3.5 w-3.5" /> Tool #2</div>
        <h2 className="mt-2 text-2xl font-extrabold">แนะนำวีซ่าที่เหมาะที่สุด</h2>
        <p className="mt-2 text-sm text-muted-foreground">ตอบ 5 ข้อ ระบบแนะนำวีซ่าที่เหมาะตาม profile (DTV, LTR, Elite, Retirement)</p>
        <div className="mt-6 space-y-5 rounded-2xl border border-border bg-card p-6">
          <div><label className="text-sm font-semibold">อายุ: {age} ปี</label><input type="range" min={18} max={75} value={age} onChange={(e) => setAge(Number(e.target.value))} className="mt-2 w-full" /></div>
          <div><label className="text-sm font-semibold">รายได้ต่อปี (USD): ${income.toLocaleString()}</label><input type="range" min={0} max={200_000} step={5_000} value={income} onChange={(e) => setIncome(Number(e.target.value))} className="mt-2 w-full" /></div>
          <div><label className="text-sm font-semibold">ทรัพย์สินรวม (USD): ${assets.toLocaleString()}</label><input type="range" min={0} max={2_000_000} step={50_000} value={assets} onChange={(e) => setAssets(Number(e.target.value))} className="mt-2 w-full" /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={remote} onChange={(e) => setRemote(e.target.checked)} className="h-4 w-4" /><span>ทำงาน Remote (จ้างโดยบริษัทต่างประเทศ)</span></label>
          <div><label className="text-sm font-semibold">ต้องการพำนัก: {years} ปี</label><input type="range" min={1} max={10} value={years} onChange={(e) => setYears(Number(e.target.value))} className="mt-2 w-full" /></div>
        </div>
      </div>
      <aside className="space-y-4">
        <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-6">
          <div className="text-xs font-bold uppercase tracking-widest text-primary">แนะนำ #1</div>
          <div className="mt-1 text-3xl font-black">{rec.top.name}</div>
          <p className="mt-2 text-sm text-muted-foreground">{rec.top.why}</p>
          <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
            <Stat Icon={Clock} label="ระยะเวลา" value={rec.top.duration} />
            <Stat Icon={Coins} label="ค่าธรรมเนียม" value={rec.top.fee} />
          </div>
          <Link to="/contact" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:bg-primary/90">ปรึกษา {rec.top.name} <ArrowRight className="h-4 w-4" /></Link>
        </div>
        {rec.alts.length > 0 && (
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">ทางเลือกอื่น</div>
            <ul className="mt-3 space-y-3 text-sm">
              {rec.alts.map((a) => (
                <li key={a.name} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" />
                  <div><div className="font-semibold">{a.name}</div><div className="text-xs text-muted-foreground">{a.why}</div></div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </aside>
    </div>
  );
}

function Stat({ Icon, label, value }: { Icon: React.ComponentType<{ className?: string }>; label: string; value: string }) {
  return (
    <div className="rounded-lg bg-card p-3">
      <div className="inline-flex items-center gap-1 text-[10px] uppercase text-muted-foreground"><Icon className="h-3 w-3" /> {label}</div>
      <div className="mt-0.5 font-bold">{value}</div>
    </div>
  );
}

const NATIONALITIES = [
  { code: "us", label: "🇺🇸 USA", embassy: "Affidavit of Eligibility to Marry — US Embassy Bangkok ฿1,750", extra: ["ต้องนัด online ล่วงหน้า"] },
  { code: "uk", label: "🇬🇧 United Kingdom", embassy: "Affirmation of Marital Status — UK Embassy Bangkok ฿3,200", extra: ["รับเฉพาะ courier ผ่าน VFS"] },
  { code: "au", label: "🇦🇺 Australia", embassy: "Certificate of No Impediment (CNI) — ขอจาก Births Deaths Marriages ก่อนเดินทาง", extra: ["ใช้เวลา 4-6 สัปดาห์ที่ออสเตรเลีย"] },
  { code: "de", label: "🇩🇪 Germany", embassy: "Ehefähigkeitszeugnis — German Embassy", extra: ["ต้องผ่าน Standesamt ในเยอรมนีก่อน"] },
  { code: "fr", label: "🇫🇷 France", embassy: "Certificat de Capacité à Mariage — French Embassy", extra: ["ใช้เวลา publication des bans 10 วัน"] },
  { code: "jp", label: "🇯🇵 Japan", embassy: "婚姻要件具備証明書 — Japanese Embassy", extra: ["มีฉบับภาษาญี่ปุ่นกำกับ"] },
  { code: "cn", label: "🇨🇳 China", embassy: "未婚证明 — Chinese Embassy + Apostille จากจีน", extra: ["ตั้งแต่ 2023 ใช้ Apostille แทน Consular"] },
  { code: "in", label: "🇮🇳 India", embassy: "Single Status Affidavit — Indian Embassy", extra: ["บางรัฐต้อง Sub-Divisional Magistrate cert"] },
  { code: "kr", label: "🇰🇷 South Korea", embassy: "혼인관계증명서 + Embassy translation", extra: ["ต้องมี Apostille จากเกาหลี"] },
  { code: "ru", label: "🇷🇺 Russia", embassy: "Справка об отсутствии брака + Russian Embassy notary", extra: ["รับรองที่ ZAGS ในรัสเซียก่อน"] },
  { code: "other", label: "🌍 ประเทศอื่น", embassy: "ติดต่อสถานทูตของสัญชาตินั้น เราช่วยประสานงานได้", extra: ["ปกติใช้ Affidavit + Embassy seal"] },
];

function districtLabel(d: string) {
  return ({ phuket: "เมืองภูเก็ต", kathu: "กะทู้", thalang: "ถลาง", bangkok: "กรุงเทพ", other: "ที่เลือก" } as Record<string, string>)[d];
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (<div><div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{title}</div><ul className="mt-2 space-y-1.5">{children}</ul></div>);
}
function Item({ children }: { children: React.ReactNode }) {
  return (<li className="flex items-start gap-2 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" /><span>{children}</span></li>);
}

function MarriageChecklist() {
  const [nat, setNat] = useState("us");
  const [district, setDistrict] = useState("phuket");
  const country = NATIONALITIES.find((n) => n.code === nat)!;
  return (
    <div id="marriage" className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary"><HeartHandshake className="h-3.5 w-3.5" /> Tool #3</div>
        <h2 className="mt-2 text-2xl font-extrabold">เช็คลิสต์เอกสารสมรสไทย-ต่างชาติ</h2>
        <p className="mt-2 text-sm text-muted-foreground">เลือกสัญชาติคู่สมรสและอำเภอที่จะจดทะเบียน ระบบสร้างเอกสารที่ต้องเตรียมให้ครบ</p>
        <div className="mt-6 space-y-5 rounded-2xl border border-border bg-card p-6">
          <div>
            <label className="text-sm font-semibold">สัญชาติคู่สมรสต่างชาติ</label>
            <select value={nat} onChange={(e) => setNat(e.target.value)} className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              {NATIONALITIES.map((n) => (<option key={n.code} value={n.code}>{n.label}</option>))}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold">อำเภอที่จะจดทะเบียน</label>
            <select value={district} onChange={(e) => setDistrict(e.target.value)} className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="phuket">เมืองภูเก็ต</option>
              <option value="kathu">กะทู้ (ป่าตอง)</option>
              <option value="thalang">ถลาง (เชิงทะเล/ในยาง)</option>
              <option value="bangkok">กรุงเทพ (50 เขต)</option>
              <option value="other">อำเภออื่น</option>
            </select>
          </div>
        </div>
      </div>
      <aside className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-6">
        <div className="text-xs font-bold uppercase tracking-widest text-primary"><ListChecks className="inline h-3.5 w-3.5" /> เอกสารที่ต้องเตรียม</div>
        <h3 className="mt-2 text-xl font-extrabold">{country.label} × ไทย</h3>
        <div className="mt-5 space-y-4 text-sm">
          <Group title="🛂 จากฝั่งต่างชาติ">
            <Item>{country.embassy}</Item>
            <Item>หนังสือเดินทาง (ต้องเหลือ &gt;6 เดือน) + สำเนา</Item>
            <Item>ใบหย่า/มรณบัตร (ถ้าเคยแต่งงาน) + แปลไทย + รับรอง MFA</Item>
            {country.extra.map((x, i) => (<Item key={i}>{x}</Item>))}
          </Group>
          <Group title="🇹🇭 จากฝั่งไทย">
            <Item>บัตรประชาชน + ทะเบียนบ้าน</Item>
            <Item>ใบหย่า/มรณบัตร (ถ้าเคยแต่งงาน)</Item>
            <Item>พยาน 2 คน (พร้อมบัตร ปชช.)</Item>
          </Group>
          <Group title="📜 ที่ทีม Thai Notary ทำให้">
            <Item>แปลเอกสารต่างชาติเป็นไทย (NAATI/MoJ)</Item>
            <Item>รับรองกงสุล MFA (กรมการกงสุล แจ้งวัฒนะ)</Item>
            <Item>นัดอำเภอ {districtLabel(district)} + พยานคนไทย 2 คน</Item>
            <Item>แปลทะเบียนสมรสกลับเป็น EN/ภาษาคู่สมรส หลังจดเสร็จ</Item>
          </Group>
        </div>
        <div className="mt-5 rounded-lg bg-card p-4 text-xs">
          <div className="font-bold">⏱ Timeline: 14-21 วันทำการ</div>
          <div className="mt-1 text-muted-foreground">💰 ค่าบริการเริ่ม ฿18,000 (ไม่รวมค่าธรรมเนียมสถานทูต)</div>
        </div>
        <Link to="/contact" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:bg-primary/90">เริ่มดำเนินการ <ArrowRight className="h-4 w-4" /></Link>
      </aside>
    </div>
  );
}
